import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, RefreshCcw } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { authAPI } from '@/lib/api';
import { toast } from 'sonner';
import { useLanguage } from '@/i18n';

export default function ResetPasswordPage() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get('token') || '', [searchParams]);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [validation, setValidation] = useState({ valid: false, email: '' });
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setValidation({ valid: false, email: '' });
        setLoading(false);
        return;
      }

      try {
        const response = await authAPI.validateResetToken(token);
        setValidation(response.data || { valid: false, email: '' });
      } catch {
        setValidation({ valid: false, email: '' });
      } finally {
        setLoading(false);
      }
    };

    validateToken();
  }, [token]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (newPassword !== confirmPassword) {
      toast.error(t('passwordReset.mismatch'));
      return;
    }

    setSubmitting(true);
    try {
      const response = await authAPI.resetPassword({
        token,
        new_password: newPassword,
        confirm_password: confirmPassword,
      });
      toast.success(response.data?.message || t('passwordReset.success'));
      navigate('/login', { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || t('passwordReset.invalidLinkDescription'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-50 via-white to-blue-50 px-6 py-12" data-testid="reset-password-page">
      <Card className="w-full max-w-md border-0 bg-white/95 shadow-2xl shadow-blue-200/50">
        <CardContent className="space-y-6 p-8">
          <div className="space-y-2 text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-blue-100 text-[#2B4DAA]">
              <Lock className="h-6 w-6" />
            </div>
            <h1 className="text-2xl font-semibold text-slate-900">{t('passwordReset.pageTitle')}</h1>
            <p className="text-sm text-slate-500">{t('passwordReset.pageDescription')}</p>
          </div>

          {loading ? (
            <div className="flex justify-center py-10" data-testid="reset-password-loading">
              <div className="h-10 w-10 animate-spin rounded-full border-2 border-slate-200 border-t-[#2B4DAA]" />
            </div>
          ) : validation.valid ? (
            <form className="space-y-4" onSubmit={handleSubmit} data-testid="reset-password-form">
              <div className="rounded-2xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-700" data-testid="reset-password-validation-banner">
                {t('passwordReset.validatedFor', { email: validation.email || '—' })}
              </div>

              <div className="space-y-2">
                <Label htmlFor="new-password-input">{t('passwordReset.newPassword')}</Label>
                <Input
                  id="new-password-input"
                  type="password"
                  value={newPassword}
                  onChange={(event) => setNewPassword(event.target.value)}
                  required
                  minLength={8}
                  data-testid="reset-password-new-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm-password-input">{t('passwordReset.confirmPassword')}</Label>
                <Input
                  id="confirm-password-input"
                  type="password"
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  required
                  minLength={8}
                  data-testid="reset-password-confirm-input"
                />
              </div>

              <p className="text-xs text-slate-500">{t('passwordReset.helper')}</p>

              <Button className="w-full" type="submit" disabled={submitting} data-testid="reset-password-submit-button">
                {submitting ? t('passwordReset.resetting') : t('passwordReset.resetButton')}
              </Button>

              <Button asChild variant="ghost" className="w-full" data-testid="reset-password-back-to-login-button">
                <Link to="/login">{t('passwordReset.backToLogin')}</Link>
              </Button>
            </form>
          ) : (
            <div className="space-y-4 text-center" data-testid="reset-password-invalid-state">
              <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-4 text-sm text-amber-700">
                <p className="font-medium">{t('passwordReset.invalidLinkTitle')}</p>
                <p className="mt-1">{t('passwordReset.invalidLinkDescription')}</p>
              </div>

              <Button asChild className="w-full" data-testid="reset-password-request-another-button">
                <Link to="/login">
                  <span className="flex items-center gap-2">
                    <RefreshCcw className="h-4 w-4" />
                    {t('passwordReset.requestAnother')}
                  </span>
                </Link>
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}